import { apiClient } from '@/auth';
import type { Meal } from './meals';

/**
 * Unified activity-feed types for the patient timeline. Server returns
 * `PatientActivityFeedRow[]` (MNT swagger:21772) — a discriminated union
 * keyed by `type`. Each row shares `id` / `timestamp` plus a row-specific
 * `data` payload.
 *
 * Subset of the per-row data fields we actually consume in the timeline
 * cards. Full schema is much wider; we cherry-pick what the UI uses.
 */

export type ExerciseTypeInResponse =
  | 'logged by cgm'
  | 'strength-resistance'
  | 'aerobic training'
  | 'balance and stability'
  | 'flexibility-stretching'
  | 'mixed cardio/strength'
  | 'other';

export type ExerciseLogData = {
  id?: number;
  exercise_date: string;
  exercise_time: string;
  exercise_type: ExerciseTypeInResponse;
  duration_min: number;
  intensity?: string | null;
  source?: string | null;
  note?: string | null;
};

export type MedicationLogData = {
  id: number;
  medication_date: string;
  medication_time: string;
  name: string;
  quantity: number;
  unit: string;
  note?: string | null;
  side_effects?: string | null;
};

/**
 * Activity-feed row `data` for `type === 'emotion-log'`. The wire model
 * is `PatientEmotionResponse` — note the field names: `emotion` is the
 * option code (string), NOT a numeric level. There is no `emotion_level`
 * or `emotion_type`; the prior shape was wrong and silently rendered
 * blank cells in DayActivityTimeline + PatientRightPanel.
 */
export type EmotionLogData = {
  id?: number;
  patient_id?: number;
  emotion_date?: string;
  emotion_time?: string | null;
  emotion_date_time: string;
  question?: string | null;
  /** Option code (e.g. "happy" / "sad" / "soft"). */
  emotion: string;
  note?: string | null;
};

export type SleepLogData = {
  sleep_date: string;
  sleep_start_date_time: string;
  sleep_end_date_time: string;
  sleep_duration_min: number;
};

export type StepsLogData = {
  steps_date: string;
  step_count: number;
};

export type MindfulnessLogData = {
  mindfulness_date: string;
  mindfulness_start_time?: string;
  mindfulness_end_time?: string;
  mindfulness_activity_type?: string | null;
  note?: string | null;
};

export type StressLogData = {
  stress_date?: string;
  stress_time?: string;
  stress_level?: number;
  note?: string | null;
};

export type EmotionalHealthLogData = {
  log_date?: string;
  log_time?: string;
  score?: number;
  note?: string | null;
};

export type ActivityFeedRowBase = {
  id: string;
  timestamp: string;
};

export type ActivityFeedRow =
  | (ActivityFeedRowBase & { type: 'meal-log'; data: Meal })
  | (ActivityFeedRowBase & { type: 'exercise-log'; data: ExerciseLogData })
  | (ActivityFeedRowBase & { type: 'medication-log'; data: MedicationLogData })
  | (ActivityFeedRowBase & { type: 'emotion-log'; data: EmotionLogData })
  | (ActivityFeedRowBase & { type: 'sleep-log'; data: SleepLogData })
  | (ActivityFeedRowBase & { type: 'steps-log'; data: StepsLogData })
  | (ActivityFeedRowBase & { type: 'mindfulness-log'; data: MindfulnessLogData })
  | (ActivityFeedRowBase & { type: 'stress-log'; data: StressLogData })
  | (ActivityFeedRowBase & { type: 'emotional-health-log'; data: EmotionalHealthLogData });

export type ActivityFeedResponse = {
  has_more?: boolean;
  next?: string;
  rows: ActivityFeedRow[];
};

/**
 * The instant to DISPLAY for an activity row.
 *
 * For `sleep-log` the server sets the row `timestamp` to the sleep END (wake
 * time), so rendering `timestamp` directly made a night's sleep read as if it
 * happened in the morning ("looks like the wake time, not sleep"). The
 * meaningful "when" for a sleep entry is the bedtime (`sleep_start_date_time`);
 * the wake time is surfaced separately as the entry's detail. Every other row
 * type uses its `timestamp` unchanged.
 */
export const displayInstantFor = (row: ActivityFeedRow): string => {
  if (row.type === 'sleep-log') {
    return row.data.sleep_start_date_time || row.timestamp;
  }
  return row.timestamp;
};

export const activityFeedApi = {
  /**
   * GET `/patient/{id}/activity_feed` (MNT swagger:7179).
   *
   * The endpoint takes:
   *   - `before` — exclusive upper-bound DATE (returns rows with timestamp
   *     strictly earlier than this day). Drives pagination too.
   *   - `limit`  — max rows returned in this page.
   *   - `filter` — opaque JSON filter; supports field-level filters that
   *     v1 actually uses (`type`, `meal-log`, and a `timestamp.lt` cursor
   *     for pagination). The compound `timestamp.or:[{gte, lte}]` shape v2
   *     was sending is NOT recognized by this endpoint (that's the
   *     reporting/nutrition_details endpoint's syntax) — when sent, the
   *     server returned zero rows, which surfaced as "No meals in this
   *     date range" on Logs v3 and missing calendar dots everywhere.
   *
   * Strategy: ask for everything strictly before `date_until + 1 day`,
   * cap at `limit` (default 500), then client-side trim to `>= date_since`.
   * Mirrors v1's `useInfiniteQuery` page-load (clinician/src/pages/
   * patient-logV2.tsx:373) collapsed into a single fetch since the
   * clinician timeline rarely needs more than 500 entries.
   */
  async list(
    patientId: number,
    params: { date_since?: string; date_until?: string; limit?: number; before?: string },
  ): Promise<ActivityFeedResponse> {
    // Bump the `before` date one day forward so the inclusive upper bound
    // the caller asked for (`date_until`) actually returns rows on that day.
    // The server treats `before` as STRICT (`<`), so `before=date_until`
    // would skip the entire last day.
    let beforeDate = params.before;
    if (!beforeDate && params.date_until) {
      const d = new Date(`${params.date_until}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        d.setDate(d.getDate() + 1);
        beforeDate = d.toISOString().slice(0, 10);
      }
    }

    try {
      const res = await apiClient.get<ActivityFeedResponse>(`/patient/${patientId}/activity_feed`, {
        params: {
          before: beforeDate,
          limit: params.limit ?? 500,
        },
      });
      if (!res.data || !Array.isArray(res.data.rows)) return { rows: [] };

      // Client-side lower-bound filter. The endpoint has no `since` param,
      // so we slice in JS. Comparing the ISO timestamp's date prefix is
      // safe because date_since is YYYY-MM-DD and the timestamp string is
      // sortable as ISO-8601.
      if (params.date_since) {
        const sinceDay = params.date_since;
        const filtered = res.data.rows.filter(
          (row) => row.timestamp.slice(0, 10) >= sinceDay,
        );
        return { ...res.data, rows: filtered };
      }
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      // 4xx → empty; some patients lack feed permissions / no data yet.
      if (status !== undefined && status >= 400 && status < 500) return { rows: [] };
      throw err;
    }
  },
};
